import React from 'react';
import { useResponsiveSize } from '@bingads-webui-react/with-responsive-size';
import {
  mergeClasses,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';

const useDefaultTemplateStyles = makeStyles({
  defaultLayout: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    '& .ui-blocker-container': {
      ...shorthands.overflow('hidden'),
      ...shorthands.flex(1),
    },
  },
  mainContainer: {
    display: 'flex',
    height: '100%',
    width: '100%',
    ...shorthands.overflow('hidden', 'auto'),
  },
  stepListContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  stepContentAndFooter: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.flex(1),
  },
  stepContentContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.flex(1),
    ...shorthands.overflow('auto'),
    ...shorthands.padding('16px', '24px'),
  },
  footerContainer: {},
  // size s classes
  'mainContainer-s': {
    flexDirection: 'column',
  },
  'stepListContainer-s': {
    flexDirection: 'row',
  },
  // size m classes
  'mainContainer-m': {
    flexDirection: 'column',
  },
  'stepListContainer-m': {
    flexDirection: 'row',
  },
});

interface DefaultTemplateProps {
  items: {
    TopBar: React.FC;
    Blocker: React.FC;
    LoadingShimmer: React.FC;
    StepList: React.FC;
    StepBar: React.FC;
    StepContainer: React.FC;
    SummaryPanel: React.FC;
    Footer: React.FC;
    CancelDialog: React.FC;
    ConfirmDialog: React.FC;
  };
}

export const DefaultTemplate: React.FC<DefaultTemplateProps> = ({ items }) => {
  const {
    TopBar,
    Blocker = React.Fragment,
    LoadingShimmer,
    StepList, // Usually vertical one
    StepBar, // Usually horizontal one for small screens
    StepContainer,
    SummaryPanel,
    Footer,
    CancelDialog,
    ConfirmDialog,
  } = items;
  const classes = useDefaultTemplateStyles();
  const displayMode = useResponsiveSize();

  const isLargeScreen = ['', 'l', 'xl', 'xxl'].includes(displayMode);
  const Stepper = isLargeScreen ? StepList : StepBar;

  return (
    <div className={mergeClasses('default-layout', classes.defaultLayout)}>
      {TopBar && <TopBar />}
      <Blocker>
        <div className={mergeClasses('main-container', classes.mainContainer, classes[`mainContainer-${displayMode}`])}>
          {Stepper
            && (
            <div className={mergeClasses('step-list-container', classes.stepListContainer, classes[`stepListContainer-${displayMode}`])} role="navigation">
              <Stepper />
            </div>
            )}
          <div
            role="main"
            className={mergeClasses('step-content-and-footer', classes.stepContentAndFooter)}
          >
            <div className={mergeClasses('step-content-container', classes.stepContentContainer)}>
              {LoadingShimmer && <LoadingShimmer />}
              <StepContainer />
            </div>
            {Footer
              && (
              <div className={mergeClasses('footer-container', classes.footerContainer)}>
                <Footer />
              </div>
              )}
          </div>
          {SummaryPanel && isLargeScreen && <SummaryPanel />}
        </div>
      </Blocker>
      {CancelDialog && <CancelDialog />}
      {ConfirmDialog && <ConfirmDialog />}
    </div>
  );
};
